import React, { useState, useCallback } from 'react';
import { AppBar, Avatar, Box, Divider, IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { Menu as MenuIcon, PersonOutline as PersonOutlineIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDashboardShell } from '../../context/DashboardShellContext';
import { getDisplayInitials, getFriendlyDisplayName } from '../../utils/displayName';
import { drawerWidth } from '../Sidebar/Sidebar';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../notifications/NotificationBell';
import InstitutionBrandRow from '../InstitutionBrandRow';
import { dashboardTooltipSurfaceSx } from '../../utils/colors';
import { fw, fs } from "../../theme/designLintTokens";
function getInitials(name, preferredName) {
  return getDisplayInitials({
    fullName: name,
    preferredName
  });
}
const Header = ({
  onDrawerToggle,
  _sidebarCollapsed = false,
  _onSidebarToggle
}) => {
  const {
    user,
    logout
  } = useAuth();
  const shell = useDashboardShell();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  const goProfile = useCallback(() => {
    handleClose();
    navigate(shell?.profilePath ?? '/profile');
  }, [handleClose, navigate, shell?.profilePath]);
  const handleLogout = useCallback(() => {
    handleClose();
    logout();
  }, [handleClose, logout]);
  const displayName = user ? getFriendlyDisplayName({
    fullName: user.name,
    preferredName: user.preferredName
  }) : '';
  return <AppBar position="fixed" color="inherit" elevation={0} sx={{
    width: {
      sm: `calc(100% - ${drawerWidth}px)`
    },
    ml: {
      sm: `${drawerWidth}px`
    },
    color: 'text.primary',
    borderBottom: 1,
    borderColor: theme => theme.palette.ef?.hair ?? 'divider',
    // D4: opaque chrome — residual glass / blur retired with A3 shell.
    bgcolor: theme => theme.palette.ef?.chrome ?? 'background.paper'
  }}>
      <Toolbar sx={{
      minHeight: {
        xs: 56,
        sm: 64
      },
      gap: 1
    }}>
        <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={onDrawerToggle} sx={{
        mr: {
          xs: 0,
          sm: 0
        },
        display: {
          sm: 'none'
        }
      }}>
          <MenuIcon />
        </IconButton>

        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: {
          xs: 1,
          sm: 1.5
        },
        flexGrow: 1,
        minWidth: 0
      }}>
          <Box aria-hidden sx={{
          width: 3,
          height: 32,
          borderRadius: 1,
          bgcolor: 'primary.main',
          flexShrink: 0,
          display: {
            xs: 'none',
            sm: 'block'
          }
        }} />
          <InstitutionBrandRow size="sm" variant="subtitle2" sx={{
          flexShrink: 0
        }} />
          {user && <Typography variant="subtitle1" noWrap component="span" sx={{
          color: 'text.primary',
          letterSpacing: '-0.01em',
          minWidth: 0
        }}>
              Welcome, {displayName}
            </Typography>}
        </Box>

        <ThemeToggle />

        {user ? <NotificationBell /> : null}

        {user && <>
            <IconButton onClick={handleOpen} size="small" aria-label="Account menu" aria-controls={open ? 'account-menu' : undefined} aria-haspopup="true" aria-expanded={open ? 'true' : undefined} sx={{
          p: 0.5,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          '&:hover': {
            bgcolor: 'action.hover'
          }
        }}>
              <Avatar alt={user.name || 'User'} sx={{
            width: 36,
            height: 36,
            fontSize: fs.s_n0_875rem,
            fontWeight: fw.semiBold,
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}>
                {getInitials(user.name, user.preferredName)}
              </Avatar>
            </IconButton>

            <Menu id="account-menu" anchorEl={anchorEl} open={open} onClose={handleClose} onClick={handleClose} anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }} transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }} slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 2,
              ...dashboardTooltipSurfaceSx
            }
          }
        }}>
              <Box sx={{
            px: 2,
            py: 1.5,
            maxWidth: 280
          }}>
                <Typography variant="subtitle1" noWrap color="text.primary" sx={{
              letterSpacing: '-0.01em'
            }}>
                  {user.name}
                </Typography>
                {user.email && <Typography variant="body2" color="text.secondary" noWrap display="block" sx={{
              mt: 0.35
            }}>
                    {user.email}
                  </Typography>}
              </Box>
              <Divider />
              <MenuItem onClick={goProfile} sx={{
            py: 1.25,
            gap: 1.5,
            typography: 'body2',
            fontWeight: fw.medium
          }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{
            py: 1.25,
            gap: 1.5,
            typography: 'body2',
            fontWeight: fw.medium
          }}>
                <LogoutIcon fontSize="small" color="action" />
                Logout
              </MenuItem>
            </Menu>
          </>}
      </Toolbar>
    </AppBar>;
};
export default Header;
